import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import cx from 'classnames';
import axios from 'axios';
import _ from 'lodash';
import {
  API_NEWCLIENT,
  API_KEY,
  API_CLIENTS_NAME,
  API_GET_PLANS,
  API_GROUPS,
  GET_PRIMARY_SERVICES,
  HOLIDAYS_LIST
} from '../../utils/constants';
import { useSelector, useDispatch } from 'react-redux';
import { generateContent } from '../../store/slices/generalState';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import './UnifiedClientOnboarding.css';
import BusinessHours from '../../pages/Client/AboutSection/BusinessHours/BusinessHours';
import Swal from 'sweetalert2';
import { setSelectedClient } from '../../store/slices/selectedClient';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import Switch from '@mui/material/Switch';

export const daysOrder = {
  Sunday: 1,
  Monday: 2,
  Tuesday: 3,
  Wednesday: 4,
  Thursday: 5,
  Friday: 6,
  Saturday: 7
};

const getHolidaysFromEnv = () => {
  const envHolidays = HOLIDAYS_LIST;
  if (!envHolidays) return [];
  try {
    // Si la variable es un JSON, parsea, si es string separado por coma, divide
    if (envHolidays.trim().startsWith('[')) {
      return JSON.parse(envHolidays);
    }
    return envHolidays
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const UnifiedClientOnboarding = ({
  callbackFromParent,
  defaultData = {},
  onClientCreated
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedClient = useSelector((state) => state.selectedClient.id);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [gettingPlans, setGettingPlans] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [gettingIndustries, setGettingIndustries] = useState(false);
  const [apiGroupsFetched, setApiGroupsFetched] = useState(false);
  const [loadPrimaryServices, setLoadPrimaryServices] = useState(false);

  const holidaysList = getHolidaysFromEnv();

  const initialValues = {
    clientName: '', // String required
    timeZone: '', // String required
    serviceArea: [], // list of Strings optional
    secondaryServiceArea: [],
    description: '', // String optional
    businessHours: {
      // Object with keys required and values for business hours as strings with format 'number/number' or just '/'
      monday: '9/18',
      tuesday: '9/18',
      wednesday: '9/18',
      thursday: '9/18',
      friday: '9/18',
      saturday: '9/18',
      sunday: '9/18'
    },
    mergeFields: {
      // Object with keys required and values as strings
      '~Address': '',
      '~Phone': '',
      '~Website': '',
      '~Email': ''
    },
    industry: '', // String required
    primaryService: [], // list of Strings optional
    additionalService: [], // list of Strings optional
    attributes: [], // list of Strings optional
    promotions: [], // list of Strings optional
    bestTimes: {
      // Object required with days as keys and strings as values (those strings have a format like '00' )
      Monday: '07',
      Tuesday: '07',
      Wednesday: '07',
      Thursday: '07',
      Friday: '07',
      Saturday: '07',
      Sunday: '07'
    },
    holidays: [], // list of strings optional
    igTags: [], // list of strings optional
    fbTags: [], // list of strings optional
    negativeKeywords: [], // list of strings required
    galleries: {
      //object with those keys and values optionals but if enabled the url is required
      stockPhotos: {
        enabled: true
      },
      appLibrary: {
        enabled: false
      }
    },
    clientFlags: [], // Array of strings for client notes/flags
    logoUrl: '', // string optional
    brand: {
      // object optional
      primaryColor: '#3B5998',
      secondaryColor: '#8392A5'
    },
    tier: '',
    automaticReviews: true,
    singlePerson: false,
    holidays: holidaysList
  };

  const userInfo = useSelector((state) => state.auth.userInfo);
  const timeZones = [
    { value: 1, label: 'Eastern Daylight Time - (GMT-4)' },
    { value: 2, label: 'Central Daylight Time - (GMT-5)' },
    { value: 3, label: 'Mountain Daylight Time - (GMT-6)' },
    { value: 4, label: 'Mountain Standard Time - (GMT-7)' },
    { value: 5, label: 'Pacific Daylight Time - (GMT-7)' },
    { value: 6, label: 'Alaska Daylight Time - (GMT-8)' },
    { value: 7, label: 'Hawaii-Aleutian Standard Time - (GMT-10)' }
  ];

  const attributeList = [
    { value: 1, label: 'Curbside Pickup' },
    { value: 2, label: 'Deliver' },
    { value: 3, label: 'Dine-in' },
    { value: 4, label: 'Drive through' },
    { value: 5, label: 'No-contact delivery' },
    { value: 6, label: 'Outdoor seating' },
    { value: 7, label: 'Takeout' },
    { value: 8, label: 'Assistive hearing loop' },
    { value: 9, label: 'Beach wheelchairs' },
    { value: 10, label: 'Mobility scooter rental' },
    { value: 11, label: 'Passenger loading area' },
    { value: 12, label: 'Wheelchair-accessible elevator' },
    { value: 13, label: 'Wheelchair-accessible entrance' },
    { value: 14, label: 'Wheelchair-accessible parking' },
    { value: 15, label: 'Wheelchair-accessible restroom' },
    { value: 16, label: 'Wheelchair-accessible seating' },
    { value: 17, label: 'Adults only' },
    { value: 18, label: 'Family-friendly' },
    { value: 19, label: 'Kid-friendly' },
    { value: 20, label: 'LGBTQ+ friendly' },
    { value: 21, label: 'Transgender safespace' },
    { value: 22, label: 'Accepting new patients' },
    { value: 23, label: 'Accepts reservations' },
    { value: 24, label: 'Appointment required' },
    { value: 25, label: 'Membership required' },
    { value: 26, label: 'Online events calendar' },
    { value: 27, label: 'Reservations recommended' },
    { value: 28, label: 'Reservations required' },
    { value: 29, label: 'Beach access' },
    { value: 30, label: 'Bicycles for rent' },
    { value: 31, label: 'Boats for rent' },
    { value: 32, label: 'Game room' },
    { value: 33, label: 'Horseback riding' },
    { value: 34, label: 'Jogging track' },
    { value: 35, label: 'Nightclub' },
    { value: 36, label: 'Scuba diving' },
    { value: 37, label: 'Snorkeling' },
    { value: 38, label: 'Tennis' },
    { value: 39, label: 'Water skiing' },
    { value: 40, label: 'Identifies as black owned' },
    { value: 41, label: 'Identifies as veteran-led' },
    { value: 42, label: 'Identifies as women-led' },
    { value: 43, label: 'Cash Only' },
    { value: 44, label: 'Checks' },
    { value: 45, label: 'Debit Cards' },
    { value: 46, label: 'NFC Mobile Payments' },
    { value: 47, label: 'American Express' },
    { value: 48, label: 'Master Card' },
    { value: 49, label: 'Visa' },
    { value: 50, label: 'Discover' },
    { value: 51, label: 'Diners club' }
  ];

  const customMultiSelectorStyle = {
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    control: (provided) => ({
      ...provided,
      width: '100%',
      minHeight: 'auto',
      height: 'auto',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: 'none',
      '&:hover': { borderColor: '#3B5998' }
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 200
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#3B5998',
      color: 'white',
      borderRadius: '6px',
      margin: '2px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'white',
      '&:hover': {
        backgroundColor: '#D9D9D9',
        color: 'black'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      minHeight: 'auto',
      maxHeight: 'none',
      overflowY: 'visible',
      overflowX: 'hidden'
    })
  };

  const validationSchema = yup.object().shape({
    clientName: yup.string().required('Client name is required'),
    timeZone: yup.string().required('Time zone is required'),
    serviceArea: yup
      .array()
      .min(1, 'Primary Service Area is required')
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚ][a-zA-Z0-9áéíóúÁÉÍÓÚ\s',.~-]*$/,
            'A service area has an invalid format'
          )
      ),
    secondaryServiceArea: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚ][a-zA-Z0-9áéíóúÁÉÍÓÚ\s',.~-]*$/,
            'A secondary service area has an invalid format'
          )
      ),
    description: yup.string(),
    businessHours: yup.object().shape({
      monday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      tuesday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      wednesday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      thursday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      friday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      saturday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        ),
      sunday: yup
        .string()
        .matches(
          /^((?:2[0-4]|1[0-9]|[0-9])\/(?:2[0-4]|1[0-9]|[0-9])|\/)$/,
          'Invalid format hour should be a number between 0-24'
        )
    }),
    mergeFields: yup.object().shape({
      '~Address': yup.string(),
      '~Phone': yup.string().required('Phone is required'),
      '~Website': yup.string(),
      '~Email': yup.string()
    }),
    industry: yup.string().required('Industry is required'),
    primaryService: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A primary service has an invalid format'
          )
      ),
    additionalService: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'An additional service has an invalid format'
          )
      ),
    attributes: yup.array().of(yup.string()),
    promotions: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A promotion has an invalid format'
          )
      ),
    holidays: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'Invalid format'
          )
      ),
    igTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    fbTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    negativeKeywords: yup.array().of(yup.string()),
    galleries: yup.object().shape({
      stockPhotos: yup.object().shape({ enabled: yup.boolean() }),
      appLibrary: yup.object().shape({ enabled: yup.boolean() })
    }),
    logoUrl: yup.string(),
    brand: yup.object().shape({
      primaryColor: yup.string(),
      secondaryColor: yup.string()
    }),
    tier: yup.string(),
    automaticReviews: yup.boolean(),
    singlePerson: yup.boolean()
  });

  const editing = Object.keys(defaultData).length > 0;

  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props} className="selected-option">
      {children}
      <div className="selected-logo">
        <Connected />
      </div>
    </components.SingleValue>
  );

  const Option = (props) => (
    <components.Option {...props}>
      <div className="country-option">
        {props.data.label}
        <Disconnected />
      </div>
    </components.Option>
  );

  const Connected = () => (
    <img src="../assets/img/Connected.png" alt="connected" />
  );

  const Disconnected = () => (
    <img src="../assets/img/Disconnected.png" alt="disconnected" />
  );

  // Reset industries when selected client changes
  useEffect(() => {
    setIndustries([]);
    setApiGroupsFetched(false);
  }, [selectedClient]);

  useEffect(() => {
    if (
      !defaultData?.primaryService ||
      defaultData.primaryService.length == 0
    ) {
      setLoadPrimaryServices(true);
    }
  }, []);

  // Load industries when userInfo is available
  useEffect(() => {
    const loadIndustries = async () => {
      if (!userInfo?.accessToken || industries.length > 0) return;

      setGettingIndustries(true);
      try {
        const response = await axios.get(API_GROUPS, {
          headers: { Authorization: `bearer ${userInfo.accessToken}` }
        });
        const groupsData = response.data.groups;
        const newIndustries = groupsData?.map((option) => ({
          value: option.id,
          label: option.groupName
        }));
        setIndustries(newIndustries || []);
      } catch (e) {
        console.error('API_GROUPS Error:', e);
      } finally {
        setGettingIndustries(false);
      }
    };

    loadIndustries();
  }, [userInfo?.accessToken, industries.length]);

  // Fetch plans and content strategies
  useEffect(() => {
    const getPlans = async () => {
      setGettingPlans(true);
      try {
        const response = await axios.get(`${API_GET_PLANS}`);
        const plans = response.data;
        const order = ['Starter Plan', 'Plus Plan', 'Pro Plan', 'Elite Plan'];
        const formattedPlans = plans.map((plan) => ({
          ...plan,
          value: plan.PK,
          label: plan.SK
        }));
        const sortedPlans = formattedPlans.sort(
          (a, b) => order.indexOf(a.label) - order.indexOf(b.label)
        );
        setPlans(sortedPlans);
      } catch (e) {
        console.error(e);
      } finally {
        setGettingPlans(false);
      }
    };

    const getIndustries = async () => {
      if (!userInfo?.accessToken) return;

      setGettingIndustries(true);
      try {
        const response = await axios.get(API_GROUPS, {
          headers: { Authorization: `bearer ${userInfo.accessToken}` }
        });
        const groupsData = response.data.groups;
        const newIndustries = groupsData?.map((option) => ({
          value: option.id,
          label: option.groupName
        }));
        setIndustries((prev) =>
          _.isEqual(prev, newIndustries) ? prev : newIndustries
        );
      } catch (e) {
        console.error('API_GROUPS Error:', e);
      } finally {
        setGettingIndustries(false);
      }
    };

    if (plans.length === 0 && !gettingPlans) {
      getPlans();
    }
    if (
      industries.length === 0 &&
      !gettingIndustries &&
      userInfo?.accessToken
    ) {
      getIndustries();
    }
  }, [userInfo?.accessToken]);

  const getNewClientsKeywords = async (id) => {
    try {
      const response = await axios.post(GET_PRIMARY_SERVICES, { id });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const handlePut = async (data = {}) => {
    setLoading(true);
    let newClientName = data.clientName.replace(/\s+/g, ' ');
    newClientName = newClientName.replace(/^\s+|\s+$/g, '');
    newClientName = newClientName.replace(/'/g, "'");

    const clientData = {
      ...data,
      id: defaultData?.id,
      clientName: newClientName
    };

    try {
      const response = await axios({
        method: 'post',
        url: API_NEWCLIENT,
        data: clientData
      });

      if (response.status === 200) {
        Swal.fire({
          type: 'success',
          title: 'Success!',
          text: `The client was ${
            clientData.id ? 'updated' : 'created'
          } successfully.`
        }).then(() => {
          callbackFromParent(clientData);

          // Activate AI generation after the alert for new customers
          if (!clientData.id && onClientCreated) {
            setTimeout(() => {
              onClientCreated(clientData);
            }, 500);
          }
        });

        if (clientData?.id) {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          let newUrl = clData?.data?.clientName
            .replace(/\s+/g, '%20')
            .replace(/\//g, '%2F')
            .replace(/'/g, '%1D')
            .replace(/'/g, '%1E');
          history.push(`/client/${newUrl}`);
          if (clData.data) {
            dispatch(setSelectedClient(clData.data));
          }
        } else {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          let newUrl = clientData.clientName
            .replace(/\s+/g, '%20')
            .replace(/\//g, '%2F')
            .replace(/'/g, '%1D')
            .replace(/'/g, '%1E');
          history.push(`/calendar/${newUrl}`);
          if (clData.data) {
            dispatch(setSelectedClient(clData.data));
            dispatch(generateContent());
          }
        }
      }
    } catch (error) {
      Swal.fire({
        type: 'error',
        title: 'Error!',
        text: 'The client was not created successfully due an error.'
      });
    } finally {
      setLoading(false);
    }
  };

  function onKeyDown(keyEvent) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  }

  return (
    <Formik
      onSubmit={handlePut}
      enableReinitialize
      validateOnBlur={true}
      initialValues={
        editing
          ? {
              ...defaultData,
              ...(!defaultData.businessHours?.monday
                ? { businessHours: initialValues.businessHours }
                : {}),
              ...(!defaultData?.brand?.primaryColor
                ? { brand: initialValues.brand }
                : {})
            }
          : initialValues
      }
      validationSchema={validationSchema}>
      {(props) => {
        const {
          handleSubmit,
          setFieldValue,
          values,
          handleChange,
          handleBlur,
          errors,
          touched
        } = props;

        const buttonLabel = editing ? 'Update Client' : 'Create Client';

        return (
          <div className="card" id="posts-section">
            <div className="card-header pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
              <h6 className="tx-uppercase tx-semibold mg-b-0">New Client</h6>
              <div className="actions clearfix">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading}
                  onClick={handleSubmit}
                  style={{
                    backgroundColor: '#0c8842',
                    borderColor: '#0c8842',
                    color: 'white'
                  }}>
                  {loading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />
                  ) : (
                    buttonLabel
                  )}
                </button>
              </div>
            </div>

            <div className="card-body pd-25">
              <Form
                autoComplete="off"
                onKeyDown={onKeyDown}
                className="needs-validation"
                noValidate>
                <div className="unified-container">
                  {/* Business Information Section */}
                  <div className="section-container">
                    <h5 className="section-title">Business Information</h5>
                    <div className="form-grid">
                      <div className="form-column">
                        <div className="form-group">
                          <label className="form-label">Business Name *</label>
                          <input
                            id="clientName"
                            type="text"
                            required
                            placeholder="Enter business name"
                            value={values?.clientName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`form-control ${
                              touched?.clientName && errors?.clientName
                                ? 'is-invalid'
                                : ''
                            }`}
                          />
                          {touched?.clientName && errors?.clientName && (
                            <div className="invalid-feedback">
                              {errors?.clientName}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">Time Zone *</label>
                          <Select
                            required
                            name="timeZone"
                            options={timeZones}
                            value={timeZones.find(
                              (option) => option.label === values?.timeZone
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue('timeZone', selectedOption.label)
                            }
                            onBlur={() =>
                              setFieldValue('timeZone', values?.timeZone)
                            }
                            className={`${
                              touched?.timeZone && errors?.timeZone
                                ? 'is-invalid'
                                : ''
                            }`}
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                borderColor:
                                  touched?.timeZone && errors?.timeZone
                                    ? '#dc3545'
                                    : '#e2e8f0',
                                '&:hover': { borderColor: '#3B5998' }
                              })
                            }}
                          />
                          {errors?.timeZone && touched?.timeZone && (
                            <div className="invalid-feedback">
                              {errors?.timeZone}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">Plan</label>
                          <Select
                            id="plan"
                            options={plans}
                            value={
                              plans.find(
                                (option) => option.value === values?.tier
                              ) || {
                                value: 'none',
                                label: 'None'
                              }
                            }
                            onChange={(selectedOption) => {
                              setFieldValue('tier', selectedOption.value);
                            }}
                            components={{ Option, SingleValue }}
                            styles={{
                              singleValue: (base) => ({
                                ...base,
                                display: 'flex',
                                alignItems: 'center'
                              })
                            }}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Primary Service Area *
                          </label>
                          <CreatableSelect
                            id="serviceArea"
                            name="serviceArea"
                            isMulti
                            isClearable={false}
                            value={values?.serviceArea?.map((servA, index) => ({
                              value: index,
                              label: servA
                            }))}
                            noOptionsMessage={() => 'Type a service area'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'serviceArea',
                                selectedOption
                                  ? selectedOption.map((servA) => servA.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a service area"
                            onBlur={() =>
                              setFieldValue('serviceArea', values?.serviceArea)
                            }
                            className={`${
                              touched?.serviceArea && errors?.serviceArea
                                ? 'is-invalid'
                                : ''
                            }`}
                            styles={{
                              ...customMultiSelectorStyle,
                              control: (provided, state) => ({
                                ...customMultiSelectorStyle.control(provided),
                                borderColor:
                                  touched?.serviceArea && errors?.serviceArea
                                    ? '#dc3545'
                                    : '#e2e8f0',
                                boxShadow:
                                  touched?.serviceArea && errors?.serviceArea
                                    ? '0 0 0 3px rgba(220, 53, 69, 0.1)'
                                    : 'none',
                                '&:hover': {
                                  borderColor:
                                    touched?.serviceArea && errors?.serviceArea
                                      ? '#dc3545'
                                      : '#3B5998'
                                }
                              })
                            }}
                          />
                          {touched?.serviceArea && errors?.serviceArea && (
                            <div className="invalid-feedback">
                              {errors?.serviceArea}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Secondary Service Area
                          </label>
                          <CreatableSelect
                            id="secondaryServiceArea"
                            name="secondaryServiceArea"
                            isMulti
                            isClearable={false}
                            value={values?.secondaryServiceArea?.map(
                              (servA, index) => ({
                                value: index,
                                label: servA
                              })
                            )}
                            noOptionsMessage={() =>
                              'Type a secondary service area'
                            }
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'secondaryServiceArea',
                                selectedOption
                                  ? selectedOption.map((servA) => servA.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a secondary service area"
                            onBlur={() =>
                              setFieldValue(
                                'secondaryServiceArea',
                                values?.secondaryServiceArea
                              )
                            }
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <div className="form-check">
                            <Switch
                              checked={values.automaticReviews}
                              onChange={(event) => {
                                setFieldValue(
                                  'automaticReviews',
                                  event.target.checked
                                );
                              }}
                              size="small"
                              color="primary"
                            />
                            <label className="form-check-label ms-2">
                              Automatic Reviews
                            </label>
                          </div>
                        </div>

                        <div className="form-group">
                          <div className="form-check">
                            <Switch
                              checked={values.singlePerson}
                              onChange={(event) => {
                                setFieldValue(
                                  'singlePerson',
                                  event.target.checked
                                );
                              }}
                              size="small"
                              color="primary"
                            />
                            <label className="form-check-label ms-2">
                              Single Person Business
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="form-column">
                        <BusinessHours
                          values={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          touched={touched}
                        />

                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <textarea
                            id="description"
                            onChange={handleChange}
                            value={values?.description}
                            onBlur={handleBlur}
                            className="form-control"
                            rows={3}
                            placeholder="Describe your business..."
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Tags and Merge Fields
                          </label>
                          <div className="merge-fields-container">
                            {['~Address', '~Phone', '~Website', '~Email'].map(
                              (field) => (
                                <div
                                  key={field}
                                  className="merge-field-container">
                                  <div className="merge-field-row">
                                    <span className="merge-field-label">
                                      {field}
                                    </span>
                                    <input
                                      id={`mergeFields.${field}`}
                                      type="text"
                                      onChange={handleChange}
                                      value={values?.mergeFields[field] || ''}
                                      className={`form-control merge-field-input ${
                                        (field === '~Phone' ||
                                          field === '~Website') &&
                                        touched?.mergeFields?.[field] &&
                                        errors?.mergeFields?.[field]
                                          ? 'is-invalid'
                                          : ''
                                      }`}
                                      placeholder={`Enter ${field}`}
                                      onBlur={handleBlur}
                                    />
                                  </div>
                                  {(field === '~Phone' ||
                                    field === '~Website') &&
                                    touched?.mergeFields?.[field] &&
                                    errors?.mergeFields?.[field] && (
                                      <div className="invalid-feedback">
                                        {errors?.mergeFields?.[field]}
                                      </div>
                                    )}
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Client Notes</label>
                          <div
                            className="client-flags-container"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '8px 16px'
                            }}>
                            {[
                              { value: 'NO_FB', label: 'No FB' },
                              { value: 'NO_AI', label: 'No AI Images' },
                              { value: 'NO_GMB', label: 'No GMB' },
                              { value: 'NO_REVIEWS', label: 'No Reviews' },
                              { value: 'NO_IG', label: 'No IG' }
                            ].map((option) => (
                              <div key={option.value} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`clientFlags.${option.value}`}
                                  checked={
                                    values?.clientFlags?.includes(
                                      option.value
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentFlags =
                                      values?.clientFlags || [];
                                    if (e.target.checked) {
                                      setFieldValue('clientFlags', [
                                        ...currentFlags,
                                        option.value
                                      ]);
                                    } else {
                                      setFieldValue(
                                        'clientFlags',
                                        currentFlags.filter(
                                          (flag) => flag !== option.value
                                        )
                                      );
                                    }
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`clientFlags.${option.value}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Strategy Section */}
                  <div className="section-container">
                    <h5 className="section-title">Content Strategy</h5>
                    <div className="form-grid">
                      <div className="form-column">
                        <div className="form-group">
                          <label className="form-label">Industry *</label>
                          <Select
                            id="industry"
                            options={industries}
                            value={industries.find(
                              (option) => option?.value === values?.industry
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue('industry', selectedOption?.value)
                            }
                            onBlur={() =>
                              setFieldValue('industry', values?.industry)
                            }
                            placeholder="Select industry"
                            isLoading={gettingIndustries}
                            className={`${
                              touched?.industry && errors?.industry
                                ? 'is-invalid'
                                : ''
                            }`}
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                borderColor:
                                  touched?.industry && errors?.industry
                                    ? '#dc3545'
                                    : '#e2e8f0',
                                '&:hover': { borderColor: '#3b5998' }
                              })
                            }}
                          />
                          {errors?.industry && touched?.industry && (
                            <div className="invalid-feedback">
                              {errors?.industry}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">Primary Services</label>
                          <CreatableSelect
                            id="primaryService"
                            name="primaryService"
                            isMulti
                            isClearable={false}
                            value={values?.primaryService?.map(
                              (servA, index) => ({
                                value: index,
                                label: servA
                              })
                            )}
                            noOptionsMessage={() => 'Type a primary service'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'primaryService',
                                selectedOption
                                  ? selectedOption.map((servA) => servA.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a primary service"
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Additional Services
                          </label>
                          <CreatableSelect
                            id="additionalService"
                            name="additionalService"
                            isMulti
                            isClearable={false}
                            value={values?.additionalService?.map(
                              (servA, index) => ({
                                value: index,
                                label: servA
                              })
                            )}
                            noOptionsMessage={() =>
                              'Type an additional service'
                            }
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'additionalService',
                                selectedOption
                                  ? selectedOption.map((servA) => servA.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type an additional service"
                            styles={customMultiSelectorStyle}
                          />
                        </div>
                      </div>

                      <div className="form-column">
                        <div className="form-group">
                          <label className="form-label">Attributes</label>
                          <Select
                            id="attributes"
                            isMulti
                            options={attributeList}
                            value={attributeList.filter((attr) =>
                              values?.attributes?.includes(attr.label)
                            )}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'attributes',
                                selectedOption
                                  ? selectedOption.map((attr) => attr.label)
                                  : []
                              );
                            }}
                            placeholder="Select attributes"
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Promotions</label>
                          <CreatableSelect
                            id="promotions"
                            name="promotions"
                            isMulti
                            isClearable={false}
                            value={values?.promotions?.map((promo, index) => ({
                              value: index,
                              label: promo
                            }))}
                            noOptionsMessage={() => 'Type a promotion'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'promotions',
                                selectedOption
                                  ? selectedOption.map((promo) => promo.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a promotion"
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Holidays</label>
                          <div
                            className="client-holidays-container"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '8px 16px'
                            }}>
                            {holidaysList.map((holiday) => (
                              <div key={holiday} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`holidays.${holiday}`}
                                  checked={
                                    values?.holidays?.includes(holiday) || false
                                  }
                                  onChange={(e) => {
                                    const currentHolidays =
                                      values?.holidays || [];
                                    if (e.target.checked) {
                                      setFieldValue('holidays', [
                                        ...currentHolidays,
                                        holiday
                                      ]);
                                    } else {
                                      setFieldValue(
                                        'holidays',
                                        currentHolidays.filter(
                                          (flag) => flag !== holiday
                                        )
                                      );
                                    }
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`holidays.${holiday}`}>
                                  {holiday}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="form-column">
                        <div className="form-group">
                          <label className="form-label">Instagram Tags</label>
                          <CreatableSelect
                            id="igTags"
                            name="igTags"
                            isMulti
                            isClearable={false}
                            value={values?.igTags?.map((tag, index) => ({
                              value: index,
                              label: tag
                            }))}
                            noOptionsMessage={() => 'Type an Instagram tag'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'igTags',
                                selectedOption
                                  ? selectedOption.map((tag) => tag.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type an Instagram tag"
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Facebook Tags</label>
                          <CreatableSelect
                            id="fbTags"
                            name="fbTags"
                            isMulti
                            isClearable={false}
                            value={values?.fbTags?.map((tag, index) => ({
                              value: index,
                              label: tag
                            }))}
                            noOptionsMessage={() => 'Type a Facebook tag'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'fbTags',
                                selectedOption
                                  ? selectedOption.map((tag) => tag.label)
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a Facebook tag"
                            styles={customMultiSelectorStyle}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Negative Keywords
                          </label>
                          <CreatableSelect
                            id="negativeKeywords"
                            name="negativeKeywords"
                            isMulti
                            isClearable={false}
                            value={values?.negativeKeywords?.map(
                              (keyword, index) => ({
                                value: index,
                                label: keyword
                              })
                            )}
                            noOptionsMessage={() => 'Type a negative keyword'}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                'negativeKeywords',
                                selectedOption
                                  ? selectedOption.map(
                                      (keyword) => keyword.label
                                    )
                                  : []
                              );
                            }}
                            options={[]}
                            placeholder="Type a negative keyword"
                            styles={customMultiSelectorStyle}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default UnifiedClientOnboarding;
